import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { apiService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

const SafetyLetterStatusSection = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userStatuses, setUserStatuses] = useState([]);
  const [displayMonth, setDisplayMonth] = useState('');
  const [noSafetyLetter, setNoSafetyLetter] = useState(false);
  const { user } = useAuth();

  // Function to fetch user statuses
  const fetchUserStatuses = async () => {
    try {
      // Don't set loading to true on refreshes to avoid UI flickering
      if (userStatuses.length === 0) {
        setLoading(true);
      }
      
      setNoSafetyLetter(false);
      
      // Force refresh display month to always show current month
      const now = new Date();
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();
      setDisplayMonth(`${monthNames[currentMonth - 1]} ${currentYear}`);
      
      // Get safety letters data
      const safetyLetters = await apiService.getAllSafetyLetters();
      
      // Explicitly look for the current month letter
      const targetLetter = safetyLetters.find(
        letter => letter.month === currentMonth && letter.year === currentYear
      );
      
      // Get users by branch
      const users = await apiService.getUsersByBranch(user.branch);
      
      // If no safety letter exists for the target month, show all users as "Not Signed"
      if (!targetLetter) {
        setNoSafetyLetter(true);
        
        const statusData = users.map(u => {
          return {
            id: u.id,
            name: u.username,
            email: u.email,
            status: 'Not Signed'
          };
        });
        
        setUserStatuses(statusData);
      } else {
        // Get safety letter signatures
        const signatures = await apiService.getSafetyLetterSignatures(targetLetter.id);
        
        // Map users to their submission status
        const statusData = users.map(u => {
          const hasSigned = signatures.some(s => s.user_id === u.id);
          
          return {
            id: u.id,
            name: u.username,
            email: u.email,
            status: hasSigned ? 'Signed' : 'Not Signed'
          };
        });
        
        setUserStatuses(statusData);
      }
    } catch (err) {
      console.error('Error fetching safety letter statuses:', err);
      setError('Failed to load safety letter statuses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch
    fetchUserStatuses();
    
    // Set up polling to refresh data every 8 seconds (matching ExpenseReportStatusSection)
    const refreshInterval = setInterval(() => {
      fetchUserStatuses();
    }, 8000);
    
    // Clean up interval on component unmount
    return () => clearInterval(refreshInterval);
  }, [user]);

  if (loading && userStatuses.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Safety Letter Status - {displayMonth}
      </Typography>
      
      {noSafetyLetter && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No safety letter submissions for {displayMonth} yet.
        </Alert>
      )}
      
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userStatuses.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell align="right">
                  <Chip 
                    label={user.status} 
                    color={user.status === 'Signed' ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
            {loading && userStatuses.length > 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SafetyLetterStatusSection; 